/**
 * CentroLogger.ts
 *
 * Module for the Centro logging
 */

import { ILogOptions, ILogPayloadData, isError, isFlight } from '@ms/centro-hvc-loader/dist/runtime/logging/log.types';
import * as Configuration from "../../Configuration/Configuration";
import * as Logging from "../../Logging/Logging";

const NONFLOODGATE_SPECIFIC_CENTRO_EVENT_SAMPLING_RATE = 0.01;

// This is the object which is used to open/close/update data to Floodgate Centro
export function centroLogging(options: ILogOptions) {
	if (isFlight(options)) { return }
	try {
		const configuration = Configuration.get();
		const { data, id, ...rest } = options;
		const persistData: Logging.ICustomProperties = {
			PromptAutoDismiss: configuration.getFloodgateInitOptions().autoDismiss?.toString(),
			IsEmailPolicyEnabled: configuration.getFloodgateInitOptions().showEmailAddress,
			Data: JSON.stringify(scrubCentroUrlData(data)),
			CentroData: JSON.stringify(rest),
		};

		if (configuration.getFloodgateSurvey()) {
			persistData.CampaignId = configuration.getFloodgateSurvey().getCampaignId();
			persistData.SurveyId = configuration.getFloodgateSurvey().getId();
			persistData.SurveyType = configuration.getFloodgateSurvey().getSurveyType();
			persistData.ClientFeedbackId = configuration.getFloodgateSurvey().getPreGeneratedClientFeedbackId();
		}

		// Sample Centro side events that are not Floodgate specific. These have a numeric ID.
		if (isNumericString(id)) {
			Logging.getLogger().logEvent({
				name: id
			}, convertCentroLogType(options, persistData), persistData, NONFLOODGATE_SPECIFIC_CENTRO_EVENT_SAMPLING_RATE);
		} else {
			Logging.getLogger().logEvent({ name: id }, convertCentroLogType(options, persistData), persistData);
		}

	}
	catch (error) {
		Logging.getLogger().logEvent(
			Logging.EventIds.Centro.Logging.Error.VALUE,
			Logging.LogLevel.Error,
			{
				ErrorMessage: "Error logging centro data" + (error && error.message ? ": " + error.message : "")
			}
		);
	}
}

const convertCentroLogType = (options: ILogOptions, persistData: Logging.ICustomProperties) => {
	let message = '';
	if (Object.prototype.hasOwnProperty.call(options, 'message') && typeof options['message'] === "string") {
		message = options['message']
	}

	if (isError(options)) {
		persistData.ErrorMessage = message
		return Logging.LogLevel.Error;
	}

	persistData.Message = message
	// Anything from Centro is considered as Critical
	return Logging.LogLevel.Critical;
}

const scrubCentroUrlData = (data: ILogPayloadData) => {

	if (data === undefined || data === null || typeof data !== "object") {
		return data;
	}

	// scrub out the properties that can be sensitive.
	data["location"] = undefined;
	data["locationAncestors"] = undefined;

	return data;
}

const isNumericString = (value: string) => {
	if (value === undefined || value === null || typeof value !== "string") {
		return false;
	}

	let valueIsNumeric = false;

	if (!Number.isNaN(Number(value))) {
		valueIsNumeric = true;
	}

	return valueIsNumeric;
}